import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const STYLE_COLOURS = { default: '#6b7280', primary: '#2563eb', success: '#16a34a', danger: '#dc2626' };
const STYLE_NAMES = { default: 'Grey (default)', primary: 'Blue', success: 'Green', danger: 'Red' };

/**
 * Settings → Message icons: the look of new-stock / new-product / discount
 * messages (HOT badge, stock & price icons, the Buy button's colour).
 * Icons are Telegram custom emoji IDs; /emojiid in the bot reports them.
 */
function TelegramMessageIcons() {
    const [v, setV] = useState({ hotEnabled: true, hotId: '', hotText: 'HOT!', addedId: '', stockId: '', priceId: '', buyStyle: 'success', buyWord: 'Buy' });
    const [styles, setStyles] = useState(['default', 'primary', 'success', 'danger']);
    const [msg, setMsg] = useState(null);

    const authFetch = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    }, []);

    useEffect(() => {
        (async () => {
            const { ok, body } = await authFetch('/admin/telegram/message-icons');
            if (ok) { setV(prev => ({ ...prev, ...(body.icons || {}) })); if (body.styles) setStyles(body.styles); }
        })();
    }, [authFetch]);

    const save = async () => {
        const { ok, body } = await authFetch('/admin/telegram/message-icons', { method: 'PUT', body: JSON.stringify(v) });
        setMsg(ok ? { ok: true, text: 'Message icons saved — the next announcement uses them.' } : { ok: false, text: body.error || 'Could not save.' });
        if (ok) setV(prev => ({ ...prev, ...body.icons }));
        setTimeout(() => setMsg(null), 6000);
    };

    const idInput = (key, label, fallback) => (
        <label>{label} <span style={{ fontWeight: 400, opacity: 0.7 }}>(empty = {fallback})</span>
            <input value={v[key] || ''} onChange={e => setV({ ...v, [key]: e.target.value.replace(/\D/g, '') })}
                placeholder="custom emoji ID, e.g. 5368324170671202286" inputMode="numeric" />
        </label>
    );

    const tag = id => id ? <span title={`custom emoji ${id}`} style={{ display: 'inline-block', padding: '0 4px', borderRadius: 4, background: 'rgba(37,99,235,0.15)', fontSize: 11 }}>✨</span> : null;

    return (
        <div className="tg-card">
            <h3>Message icons</h3>
            <p className="tg-sub">
                The look of the messages buyers get for <b>new stock</b>, <b>new products</b> and <b>discounts</b>: a HOT! badge,
                the product's brand logo, icons on the stock and price lines, and a coloured Buy button with the logo on it.
                Icons are Telegram <b>custom emoji</b>. To reuse icons you like from another shop, send <code>/emojiid</code> to
                your bot, forward it their message, and paste the IDs it replies with here. Brand logos come from each
                category's icon (Categories tab → upload an icon).
            </p>
            {msg && <div className={`tg-note ${msg.ok ? 'ok' : 'bad'}`}>{msg.text}</div>}

            <div className="tg-flags" style={{ marginBottom: 12 }}>
                <label>
                    <input type="checkbox" checked={v.hotEnabled !== false} onChange={e => setV({ ...v, hotEnabled: e.target.checked })} />
                    {' '}Show a HOT! badge at the top of these messages
                </label>
            </div>
            <div className="tg-grid">
                {idInput('hotId', 'HOT badge icon', '🔥')}
                <label>Badge text <span style={{ fontWeight: 400, opacity: 0.7 }}>(clear it if the icon already says HOT!)</span>
                    <input value={v.hotText ?? ''} maxLength={20} onChange={e => setV({ ...v, hotText: e.target.value })} placeholder="HOT!" />
                </label>
            </div>
            <div className="tg-grid">
                {idInput('stockId', 'Stock icon', '📦')}
                {idInput('priceId', 'Price icon', '💵')}
            </div>
            <div className="tg-grid">
                {idInput('addedId', '"Added" icon (new stock)', '➕')}
                <label>Buy button colour
                    <select className="tg-input" value={v.buyStyle} onChange={e => setV({ ...v, buyStyle: e.target.value })}>
                        {styles.map(s => <option key={s} value={s}>{STYLE_NAMES[s] || s}</option>)}
                    </select>
                </label>
            </div>
            <label>Buy button word
                <input value={v.buyWord || ''} maxLength={16} onChange={e => setV({ ...v, buyWord: e.target.value })} placeholder="Buy" />
            </label>

            <div style={{ margin: '14px 0' }}>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Preview (custom icons show as ✨ here; buyers see the real icon)</div>
                <div style={{ display: 'inline-block', minWidth: 260, maxWidth: 380, padding: '10px 14px', borderRadius: 12, background: 'rgba(0,0,0,0.05)', lineHeight: 1.6 }}>
                    {v.hotEnabled !== false && <div>{v.hotId ? tag(v.hotId) : '🔥'} {v.hotText && <b style={{ color: '#dc2626' }}>{v.hotText}</b>}</div>}
                    {v.hotEnabled !== false && <br />}
                    <div>🎬 <b>Prime Video 6M</b> — 6 months <b>NEW STOCK</b></div>
                    <br />
                    <div>{v.addedId ? tag(v.addedId) : '➕'} Added: <b>20</b></div>
                    <div>{v.stockId ? tag(v.stockId) : '📦'} Stock: <b>32</b></div>
                    <div>{v.priceId ? tag(v.priceId) : '💵'} Price: <b>$2.50</b></div>
                    <div style={{ marginTop: 8, textAlign: 'center', padding: 7, borderRadius: 8, color: '#fff', background: STYLE_COLOURS[v.buyStyle] || '#16a34a' }}>
                        🎬 {v.buyWord || 'Buy'} Prime Video 6M
                    </div>
                </div>
            </div>

            <button className="tg-btn primary" onClick={save}>Save message icons</button>
        </div>
    );
}

export default TelegramMessageIcons;
